import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { adminGetDefaultConfiguration } from '@sdk/managers/AdminManager';
import toast from 'react-hot-toast';
import { endPoll } from '@sdk/managers/PollsManager';
import {
  FieldNamesMarkedBoolean,
  FormProvider,
  useForm,
  useFormState,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  addMonths,
  addMinutes,
  isFuture,
  isPast,
  addDays,
  differenceInMinutes,
  addWeeks,
} from 'date-fns';
import { mutate } from 'swr';

import {
  createGroupPoll,
  createIndividualPoll,
  createCompanyGroupPoll,
  createCompanyPoll,
  getPollDetail,
  editPoll,
  editSubPoll,
  createSubPoll,
  canUserCreatePremiumPoll,
} from '@sdk/managers/PollsManager';
import {
  CompanyRole,
  CreatePollInput,
  CreateSubPollInput,
  Currency,
  PollOptionInput,
  PollStatus,
  Tier,
  UpdatePollInput,
  UpdateSubPollInput,
} from '@sdk/API';

import { getImageUrl, uploadImage } from '@utils/image';
import { parseEmbedUrl } from '@utils/Embed';
import { mergeDateTime } from '@utils/Date';
import { PollDetail } from '@utils/Poll';
import { dirtyValues } from '@utils/Form';

import { CREATE_POLL_SCHEMA, CreatePollData } from './types';

import StepOne from './StepOne';
import StepTwo from './StepTwo';
import Success from './Success';

import ConfirmDialog from '@components/elements/ConfirmDialog';
import { useAuth } from '@hooks/Auth';
import { Detail, Poll } from '@sdk/models';
import { GraphQLResult } from '@aws-amplify/api';
import { votePoll } from '@sdk/managers/VotesManager';
import { listIdeaCategorys, listIdeaHashtags } from '@sdk/managers/IdeaManager';

const STEP_ONE_FIELDS = [
  'title',
  'period',
  'description',
  'pledgePeriod',
  'pledgeStart',
  'pledgeEnd',
  'password',
];

function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function lowercaseFirstLetter(string: string) {
  return string.charAt(0).toLowerCase() + string.slice(1);
}

const getPollById = async (pollId: string): Promise<PollDetail> => {
  const res: any = await getPollDetail(
    [
      'id',
      'title',
      'question',
      'description',
      'grc',
      'maxVote',
      'isNotExact',
      'category',
      'hashtag',
      'division',
      'createdAt',
      'companyID',
      'image { key bucket region }',
      'video',
      'hasPledge',
      'startTime',
      'endTime',
      'pledgePeriod1StartTime',
      'pledgePeriod1EndTime',
      'pledgePeriod2StartTime',
      'pledgePeriod2EndTime',
      'isGroup',
      'private',
      'password',
      'status',
      'themeID',
      'isPremium',
      {
        options: [
          'id',
          'title',
          'description',
          {
            detail: [
              'image { key bucket region }',
              'video',
              'price',
              'currency',
            ],
          },
        ],
        winOption: [
          'id',
          'title',
          'description',
          {
            detail: [
              'image { key bucket region }',
              'video',
              'price',
              'currency',
            ],
          },
        ],
      },
      { voteResults: [{ items: ['id', 'count'] }] },
    ],
    pollId
  );

  const data: PollDetail = {
    poll: res.data?.result,
  };

  if (res.data?.subPolls?.data?.result?.items)
    data.subPolls = res.data?.subPolls?.data?.result?.items;
  if (!data.poll) throw new Error('Failed to fetch poll.');
  // if (data.subPolls)
  //   data.subPolls.sort((a, b) => {
  //     if (!b.createdAt || !a.createdAt) return 0;
  //     if (!b.orderID || !a.orderID)
  //       return Date.parse(b.createdAt) - Date.parse(a.createdAt);
  //     return (
  //       b.orderID - a.orderID ||
  //       Date.parse(b.createdAt) - Date.parse(a.createdAt)
  //     );
  //   });

  return data;
};

const formatPollQuestions = (
  data: CreatePollData,
  options: {
    imagePrefix: string;
  }
) => {
  console.log('format', { data });

  return Promise.all(
    data.questions.map(async (q, qnIndex) => {
      return {
        subPollId: q.subPollId,
        orderID: qnIndex,
        question: q.question,
        private: 1,
        maxVote: q.maxVote,
        isNotExact: q.isNotExact,
        options: await Promise.all(
          q.options.map(async (opt, optIndex) => {
            const detail: PollOptionInput['detail'] = {};

            if (opt.embed) detail.video = parseEmbedUrl(opt.embed);
            if (opt.price !== undefined && opt.price !== null) {
              detail.price = opt.price.replace('$', '').replaceAll(',', '');
              detail.currency = (opt.currency as Currency) || Currency.SGD;
            }

            const imageFile = opt.image;
            if (imageFile && typeof imageFile !== 'string') {
              const image = await uploadImage(
                imageFile,
                `${options.imagePrefix}-qn-${qnIndex}-option-${optIndex}`
              );
              if (image) detail.image = image;
            }

            if (
              detail.price !== undefined &&
              detail.price !== null &&
              !detail.currency
            ) {
              detail.currency = (opt.currency as Currency) || Currency.SGD;
            }

            return {
              id: optIndex.toString(),
              title: opt.title,
              description: opt.description || '',
              detail,
            };
          })
        ),
      };
    })
  );
};

const checkTriggerReset = (
  data: CreatePollData,
  dirtyFields: FieldNamesMarkedBoolean<CreatePollData>,
  pollData: PollDetail,
  oldData: any
): boolean => {
  let willReset = false;
  // Changes to any of these fields
  if (
    dirtyFields.private ||
    dirtyFields.questions ||
    dirtyFields.deletedQuestions ||
    dirtyFields.group ||
    dirtyFields.maxVote ||
    dirtyFields.isNotExact ||
    oldData.maxVote !== data.maxVote ||
    oldData.isNotExact !== data.isNotExact ||
    oldData.questions.length !== data.questions.length ||
    !!data.questions.find(
      (item, i) =>
        item.maxVote !== oldData.questions[i].maxVote ||
        item.isNotExact !== oldData.questions[i].isNotExact
    )
  ) {
    willReset = true;
  } else if (dirtyFields.period) {
    const newStart = mergeDateTime(
      data.period.startDate,
      data.period.startTime
    );
    const newEnd = mergeDateTime(data.period.endDate, data.period.endDate);
    const currentStart = new Date(pollData.poll.startTime);
    const currentEnd = new Date(pollData.poll.endTime);

    if (currentEnd > newEnd || newStart > currentStart) {
      willReset = true;
    }

    if (isFuture(currentStart) && isPast(newStart)) {
      // Poll not started and new Start is in past
      willReset = true;
    } else if (isPast(currentStart) && isFuture(newStart)) {
      // Poll started and new Start is in future
      willReset = true;
    } else if (isFuture(currentEnd) && isPast(newEnd)) {
      // Poll has not ended, but new End is in past
      willReset = true;
    } else if (isPast(currentEnd) && isFuture(newEnd)) {
      // Poll has ended, but new End is in future
      willReset = true;
    }
  }
  return willReset;
};
const randomColors = [
  '#2EFFE4',
  '#FF46FF',
  '#FFB400',
  '#FF2300',
  '#FFFF00',
  '#8401FF',
  '#28FF2F',
].filter((item) => item !== localStorage.getItem('randomColor'))[
  Math.floor(Math.random() * (localStorage.getItem('randomColor') ? 6 : 7))
];
localStorage.setItem('randomColor', randomColors);

const updatePoll = async (
  pollId: string,
  data: CreatePollData,
  dirtyFields: FieldNamesMarkedBoolean<CreatePollData>,
  pollData: PollDetail,
  dbUser: any,
  companyId?: string,
  oldData?: any
) => {
  // if (data.isPremium && pollData.poll.isPremium === false) {
  //   const canCreatePremium = await canUserCreatePremiumPoll(dbUser.id);
  //   console.log({ canCreatePremium });
  //   if (!canCreatePremium)
  //     return toast.error('You do not have any premium polls left');
  // }

  const values: Partial<CreatePollData> = dirtyValues(dirtyFields, data);
  console.log(
    'updatePoll',
    { data },
    { dirtyFields },
    { values },
    { pollData }
  );

  const mainPollPayload: UpdatePollInput = {
    id: pollId,
  };

  const now = new Date().getTime();

  const imagePrefix = `${now}-${data.title.split(' ').join('_')}`;
  if (typeof values.image !== 'string') {
    if (values.image?.[0]) {
      mainPollPayload.image = await uploadImage(
        data.image[0],
        `${imagePrefix}-image`
      );
    } else {
      // mainPollPayload.image = POLL_PLACEHOLDER_IMAGE;
    }
  }

  const [mainPoll, ...subPolls] = await formatPollQuestions(data, {
    imagePrefix,
  });

  console.log({ mainPoll, subPolls });

  if (dirtyFields.password) {
    mainPollPayload.password = data.password || null;
  }
  mainPollPayload.companyID = !!data.company ? companyId : null;
  mainPollPayload.private = parseInt(data.private);
  if (dirtyFields.private) {
    mainPollPayload.private = parseInt(data.private);
  }
  if (dirtyFields.title) {
    mainPollPayload.title = data.title;
  }
  mainPollPayload.category = data.category;
  mainPollPayload.hashtag = data.hashtag;
  mainPollPayload.maxVote = data.maxVote || 1;
  mainPollPayload.isNotExact = data.isNotExact || false;
  if (dirtyFields.description) {
    mainPollPayload.description = data.description;
  }
  if (dirtyFields.group !== undefined) {
    mainPollPayload.isGroup = data.group;
  }
  if (dirtyFields.isPremium || data.isPremium !== pollData.poll.isPremium) {
    mainPollPayload.isPremium = data.isPremium;
  }

  if (dirtyFields.grc || dirtyFields.division) {
    if (data.grc && data.grc !== 'Any') {
      mainPollPayload.grc = data.grc;
    } else {
      mainPollPayload.grc = null;
    }
    if (
      mainPollPayload.grc !== undefined &&
      data.division &&
      data.division !== 'Any'
    ) {
      mainPollPayload.division = data.division;
    } else {
      mainPollPayload.division = null;
    }
  }

  if (dirtyFields.pledging !== undefined) {
    mainPollPayload.hasPledge = data.pledging;
    if (mainPollPayload.hasPledge) {
      const pledgePeriod1StartTime = mergeDateTime(
        data.pledgePeriod.startDate,
        data.pledgePeriod.startTime
      );

      const pledgePeriod1EndTime = mergeDateTime(
        data.pledgePeriod.endDate,
        data.pledgePeriod.endTime
      );
      if (pledgePeriod1StartTime)
        mainPollPayload.pledgePeriod1StartTime =
          pledgePeriod1StartTime.toISOString();
      if (pledgePeriod1EndTime)
        mainPollPayload.pledgePeriod1EndTime =
          pledgePeriod1EndTime.toISOString();
    }
  }
  if (dirtyFields.video) {
    mainPollPayload.video = data.video ? parseEmbedUrl(data.video) : '';
  }

  const start = mergeDateTime(data.period.startDate, data.period.startTime);
  const end = mergeDateTime(data.period.endDate, data.period.endTime);

  if (data.pledging && dirtyFields.pledgePeriod) {
    const pledgePeriod1StartTime = mergeDateTime(
      data.pledgePeriod.startDate,
      data.pledgePeriod.startTime
    );

    const pledgePeriod1EndTime = mergeDateTime(
      data.pledgePeriod.endDate,
      data.pledgePeriod.endTime
    );
    if (pledgePeriod1StartTime)
      mainPollPayload.pledgePeriod1StartTime =
        pledgePeriod1StartTime.toISOString();
    if (pledgePeriod1EndTime)
      mainPollPayload.pledgePeriod1EndTime = pledgePeriod1EndTime.toISOString();
  }

  if (dirtyFields.period) {
    // Should poll be reset?
    mainPollPayload.startTime = start.toISOString();
    mainPollPayload.endTime = end.toISOString();
  }
  console.log(dirtyFields?.questions);
  if (dirtyFields?.questions?.[0]) {
    mainPollPayload.question = mainPoll.question;
    mainPollPayload.options = mainPoll.options.map((opt, optIndex) => {
      const formattedOpt: PollOptionInput = {
        id: opt.id,
      };

      formattedOpt.description = dirtyFields?.questions?.[0]?.options?.[
        optIndex
      ]?.description
        ? opt.description
        : pollData?.poll.options?.[optIndex]?.description;
      formattedOpt.title = dirtyFields?.questions?.[0]?.options?.[optIndex]
        ?.title
        ? opt.title
        : pollData?.poll.options?.[optIndex]?.title;

      formattedOpt.detail = pollData.poll.options?.[optIndex]
        ?.detail as Detail & { currency?: Currency };
      if (!formattedOpt.detail)
        formattedOpt.detail = {
          currency: Currency.SGD,
        };

      if (dirtyFields?.questions?.[0]?.options?.[optIndex]?.embed)
        formattedOpt.detail.video = opt.detail.video;
      if (dirtyFields?.questions?.[0]?.options?.[optIndex]?.price) {
        formattedOpt.detail.price =
          opt.detail.price?.replace('$', '').replaceAll(',', '') || '0';
        formattedOpt.detail.currency = opt.detail.currency || Currency.SGD;
      }
      if (dirtyFields?.questions?.[0]?.options?.[optIndex]?.image) {
        formattedOpt.detail.image = opt.detail.image;
      }

      if (
        formattedOpt.detail.price !== undefined &&
        formattedOpt.detail.price !== null &&
        !formattedOpt.detail.currency
      ) {
        formattedOpt.detail.currency = opt.detail.currency || Currency.SGD;
      }

      if (
        formattedOpt.detail &&
        Object.keys(formattedOpt.detail).length === 0
      ) {
        delete formattedOpt.detail;
      }

      return formattedOpt;
    });
  }

  console.log(
    'editPoll',
    { mainPollPayload },
    Object.keys(mainPollPayload).length
  );

  if (Object.keys(mainPollPayload).length > 1) {
    await editPoll(
      ['id'],
      pollId,
      {
        input: mainPollPayload,
      },
      dbUser.username
    );
  }

  if (data.group) {
    // Remove first (main poll)
    dirtyFields.questions?.shift();

    if (
      (dirtyFields.questions?.length || 0) > 0 ||
      oldData.questions.length !== data.questions.length ||
      !!data.questions.find(
        (item, i) =>
          item.maxVote !== oldData.questions[i].maxVote ||
          item.isNotExact !== oldData.questions[i].isNotExact
      )
    ) {
      console.log('SubPolls has changes', subPolls);

      // Poll will be reset
      await Promise.all(
        subPolls.map(async (subPoll, index) => {
          let matchSubPoll: Poll[] = [];
          if (subPoll.subPollId !== undefined) {
            matchSubPoll =
              pollData?.subPolls?.filter((sp) => sp.id === subPoll.subPollId) ||
              [];
          }
          const subPollData = matchSubPoll[0];

          if (subPoll.subPollId === undefined || subPollData === undefined) {
            const subPollPayload: CreateSubPollInput = {
              private: 1,
              maxVote: +subPoll.maxVote,
              isNotExact: subPoll.isNotExact,
              orderID: index + 1, // + 1 to skip main poll
              question: subPoll.question,
              options: subPoll.options,
            };
            // Create subpoll
            console.log('createSubPoll', { subPollPayload });
            return createSubPoll(pollId, subPollPayload);
          } else {
            const subPollDirtyFields = dirtyFields?.questions?.[index];

            const subPollHasUpdate =
              subPollDirtyFields?.options ||
              subPollDirtyFields?.question ||
              oldData.questions.length !== data.questions.length ||
              !!data.questions.find(
                (item, i) =>
                  item.maxVote !== oldData.questions[i].maxVote ||
                  item.isNotExact !== oldData.questions[i].isNotExact
              ) ||
              false;

            if (subPollHasUpdate) {
              const subPollOptions: UpdateSubPollInput['options'] =
                subPoll.options.map((opt, optIndex) => {
                  const formattedOpt: PollOptionInput = {
                    id: opt.id,
                  };

                  const optDirtyFields =
                    subPollDirtyFields?.options?.[optIndex];

                  formattedOpt.description = optDirtyFields?.description
                    ? opt.description
                    : subPollData?.options?.[optIndex]?.description;
                  formattedOpt.title = optDirtyFields?.title
                    ? opt.title
                    : subPollData?.options?.[optIndex]?.title;

                  // const image = dirtyFields?.images?.[index]?.options?.[
                  //   optIndex
                  // ]
                  //   ? opt.detail?.image
                  //   : subPollData?.options?.[optIndex]?.detail?.image;

                  // const video = dirtyFields?.questions?.[index]?.options?.[
                  //   optIndex
                  // ]?.embed
                  //   ? opt.detail?.video
                  //   : subPollData?.options?.[optIndex]?.detail?.video;

                  formattedOpt.detail = subPollData?.options?.[optIndex]
                    ?.detail as Detail & { currency?: Currency };
                  if (optDirtyFields?.embed)
                    formattedOpt.detail.video = opt.detail.video;
                  if (optDirtyFields?.image) {
                    formattedOpt.detail.image = opt.detail.image;
                  }

                  return formattedOpt;
                });

              const subPollPayload: UpdateSubPollInput = {
                id: subPoll.subPollId,
                maxVote: subPoll.maxVote || 1,
                isNotExact: subPoll.isNotExact || false,
                question: subPoll.question,
                options: subPollOptions,
              };

              console.log('editSubPoll', subPoll.question, { subPollPayload });

              return editSubPoll(['id'], subPoll.subPollId, {
                input: subPollPayload,
              });
            } else {
              return;
            }
          }
        })
      );
    }

    if (data.deletedQuestions) {
      await Promise.all(
        data.deletedQuestions.map((deletedQuestion) => {
          console.log('delete subPoll', deletedQuestion.subPollId);
          if (!deletedQuestion?.subPollId) return undefined;

          return editSubPoll(['id'], deletedQuestion.subPollId, {
            input: {
              id: deletedQuestion.subPollId,
              status: 'CLOSED' as PollStatus,
            },
          });
        })
      );
    }
  } else {
    await Promise.all(
      subPolls.map((question) => {
        if (question.subPollId === undefined) {
          return undefined;
        }

        return editSubPoll(['id'], question.subPollId, {
          input: {
            id: question.subPollId,
            status: 'CLOSED' as PollStatus,
          },
        });
      })
    );
  }

  return pollId;
};

const PollCreatePage: React.FC<{
  edit?: boolean;
  isPledge?: boolean;
}> = ({ isPledge = false }) => {
  const {
    params: { pollId, duplicateId },
  } = useRouteMatch<{
    pollId: string;
    duplicateId: string;
  }>();
  const history = useHistory<any>();
  const { user, dbUser, company, companyAdmin, refetch } = useAuth();
  const [pollData, setPollData] = useState<PollDetail>();
  const isEdit = useMemo(() => !!pollId, [pollId]);

  const [isSubmitting, setSubmitting] = useState<boolean>(false);

  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    onConfirm?: () => any;
    onCancel?: () => any;
  }>({
    visible: false,
  });

  const [deleteModal, setDeleteModal] = useState<{
    visible: boolean;
    onConfirm?: () => any;
    onCancel?: () => any;
  }>({
    visible: false,
  });
  const [createdPoll, setCreatedPoll] = useState<any>();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [categories, setCategories] = useState<
    {
      id: string;
      title: string;
    }[]
  >([]);
  const [hashtags, setHashtags] = useState<
    | {
        id: string;
        title: string;
      }[]
    | undefined
  >();
  const [oldData, setOldData] = useState({
    maxVote: 1,
    isNotExact: false,

    questions: [
      {
        question: '',
        options: [
          {
            title: '',
          },
          {
            title: '',
          },
        ],
        maxVote: 1,
        isNotExact: false,
      },
    ] as CreatePollData['questions'],
  });
  const form = useForm<CreatePollData>({
    reValidateMode: 'onChange',
    resolver: zodResolver(CREATE_POLL_SCHEMA),
    defaultValues: {
      private: '0',
      company: false,
      pledging: false,
      themeID: 'default',
      hashtag: [],
      toPledge: false,
      maxVote: 1,
      isNotExact: false,
      image: [],
      period: {
        immediate: false,
        focusedInput: 'startDate',
        startDate: new Date(),
        endDate:
          (dbUser?.tier || Tier.FREE) !== Tier.FREE
            ? addMonths(new Date(), 1)
            : addMinutes(new Date(), 23 * 60),
        startTime: addMinutes(new Date(), 10),
        endTime: addMinutes(new Date(), 5),
      },
      pledgePeriod: {
        immediate: false,
        focusedInput: 'startDate',
        startDate:
          (dbUser?.tier || Tier.FREE) !== Tier.FREE
            ? addMonths(new Date(), 1)
            : addMinutes(new Date(), 23 * 60),
        endDate: addDays(
          (dbUser?.tier || Tier.FREE) !== Tier.FREE
            ? addMonths(new Date(), 1)
            : addMinutes(new Date(), 23 * 60),
          7
        ),
        startTime: addMinutes(new Date(), 6),
        endTime: addMinutes(new Date(), 0),
      },
      group: false,
      questions: isPledge
        ? [
            {
              question: '',
              options: [
                {
                  title: '',
                },
              ],
              maxVote: 1,
              isNotExact: false,
            },
          ]
        : [
            {
              question: '',
              options: [
                {
                  title: '',
                },
                {
                  title: '',
                },
              ],
              maxVote: 1,
              isNotExact: false,
            },
          ],
      deletedQuestions: [],
    },
  });

  const { dirtyFields } = useFormState({
    control: form.control,
  });

  console.log(dirtyFields);

  useEffect(() => {
    const startDate =
      (dbUser?.tier || Tier.FREE) !== Tier.FREE
        ? addMonths(new Date(), 1)
        : addMinutes(new Date(), 23 * 60);
    const endDate = addDays(
      (dbUser?.tier || Tier.FREE) !== Tier.FREE
        ? addMonths(new Date(), 1)
        : addMinutes(new Date(), 23 * 60),
      7
    );
    console.log(endDate);
    // startTime: addMinutes(new Date(), 6),
    // endTime: addMinutes(new Date(), 0),
    form.setValue(
      'pledgePeriod.startDate',
      form.getValues('toPledge') ? addMinutes(new Date(), 60) : startDate
    );
    form.setValue(
      'pledgePeriod.endDate',
      form.getValues('toPledge') ? addWeeks(new Date(), 1) : endDate
    );
    if (!form.getValues('toPledge')) {
      form.setValue('pledgePeriod.startTime', addMinutes(new Date(), 60));
    }
    // form.setValue('pledgePeriod.startTime', addMinutes(new Date(), 60));
    // form.setValue('pledgePeriod.endTime', addWeeks(new Date(), 1));
  }, [form.getValues('toPledge')]);

  useEffect(() => {
    if (isPledge) {
      form.setValue('toPledge', true);
      form.setValue('pledging', true);
      form.setValue('group', false);
      form.setValue('period.startDate', new Date());
      form.setValue('period.endDate', new Date());
      form.setValue('period.startTime', new Date());
      form.setValue('period.endTime', new Date());
    }
    if (!isEdit || !dbUser || !pollData) return;
    const isOwner = pollData.poll.owner === dbUser.id;
    const hasCompanyPermission =
      pollData.poll.companyID && company
        ? pollData.poll.companyID === company.companyID &&
          (company?.role === CompanyRole.ADMIN ||
            company?.role === CompanyRole.REPRESENTATIVE)
        : false;

    if (!isOwner && pollData.poll.companyID && !hasCompanyPermission) {
      toast.error('You do not have permission to edit this Poll');
      history.replace(`/poll/${pollData.poll.id}`);
    }
  }, [pollData, dbUser, company, history, isEdit]);

  const getCategory = async () => {
    try {
      const res = (await listIdeaCategorys([{ items: ['title', 'id'] }], {
        filter: {
          edu: {
            ne: true,
          },
        },
      })) as any;
      setCategories(res?.data?.result?.items || []);
      if (res?.data?.result?.items.length) {
        form.setValue('category', res?.data?.result?.items[0].title);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getHashtag = async () => {
    try {
      const res = (await listIdeaHashtags([{ items: ['title', 'id'] }])) as any;
      setHashtags(res?.data?.result?.items || []);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getCategory();
    getHashtag();
  }, []);

  const [r, setR] = useState(!(pollId || duplicateId));

  const createPoll = async (
    data: CreatePollData,
    dbUser,
    companyId,
    adminUsername
  ) => {
    if (data.isPremium) {
      const canCreatePremium = await canUserCreatePremiumPoll(dbUser.id);
      console.log({ canCreatePremium });
      if (!canCreatePremium)
        return toast.error('You do not have any premium polls left');
    }
    const now = new Date();
    const start = mergeDateTime(
      data.period.startDate,
      data.period.immediate ? now : data.period.startTime
    );
    const end = mergeDateTime(data.period.endDate, data.period.endTime);
    const imagePrefix = `${now.getTime()}-${data.title.split(' ').join('_')}`;
    let pollImage;

    if (
      data.isEdit &&
      typeof data.image[0] === 'string' &&
      pollData?.poll &&
      pollData?.poll?.image?.key &&
      pollData?.poll?.image?.bucket &&
      pollData?.poll?.image?.region
    ) {
      pollImage = {
        key: pollData?.poll?.image?.key || '',
        bucket: pollData?.poll?.image?.bucket || '',
        region: pollData?.poll?.image?.region || '',
      };
    } else {
      if (data.image[0]) {
        pollImage = await uploadImage(data.image[0], `${imagePrefix}-image`);
      } else {
        const configRes =
          (await adminGetDefaultConfiguration()) as GraphQLResult<{
            result: {
              items: any[];
            };
          }>;
        const config = configRes.data?.result.items?.[0];
        pollImage = config?.pollImage;
      }
    }

    const [mainPoll, ...subPolls] = await formatPollQuestions(data, {
      imagePrefix,
    });

    const mainPollPayload: CreatePollInput = {
      title: data.title,
      password: data.password,
      category: data.category,
      hashtag: data.hashtag,
      question: mainPoll.question,
      options: mainPoll.options,
      maxVote: +(data.maxVote || 1),
      isNotExact: data.isNotExact || false,
      startTime: start.toISOString(),
      endTime: end.toISOString(),
      description: data.description,
      private: parseInt(data.private),
      hasPledge: data.pledging || false,
      grc: data.grc === 'Any' ? undefined : data.grc,
      division: data.division === 'Any' ? undefined : data.division,
      themeID: data.themeID,
      isPremium: data.isPremium,
    };
    if (data.company) {
      if (!companyId) return toast.error('You do not belong to a company');
      mainPollPayload.companyID = companyId;
    }

    if (data.pledging) {
      const pledgePeriod1StartTime = mergeDateTime(
        data.pledgePeriod.startDate,
        data.pledgePeriod.immediate ? now : data.pledgePeriod.startTime
      );

      const pledgePeriod1EndTime = mergeDateTime(
        data.pledgePeriod.endDate,
        data.pledgePeriod.endTime
      );
      if (pledgePeriod1StartTime)
        mainPollPayload.pledgePeriod1StartTime =
          pledgePeriod1StartTime.toISOString();
      if (pledgePeriod1EndTime)
        mainPollPayload.pledgePeriod1EndTime =
          pledgePeriod1EndTime.toISOString();
    }

    if (data.video) mainPollPayload.video = parseEmbedUrl(data.video);

    mainPollPayload.image = (await pollImage) ? await pollImage : undefined;

    if (!mainPollPayload.image) {
      const configRes =
        (await adminGetDefaultConfiguration()) as GraphQLResult<{
          result: {
            items: any[];
          };
        }>;
      const config = configRes.data?.result.items?.[0];
      mainPollPayload.image = config?.pollImage;
    }

    if (data.group) {
      console.log('createGroupPoll', { mainPollPayload, subPolls });
      let createPollAction;
      if (data.company) {
        if (!companyId) return toast.error('You do not belong to a company');
        createPollAction = createCompanyGroupPoll(
          mainPollPayload,
          subPolls,
          companyId,
          adminUsername
        );
      } else {
        createPollAction = createGroupPoll(
          mainPollPayload,
          subPolls,
          dbUser.username
        );
      }
      const res: any = await toast.promise(createPollAction, {
        loading: `Creating ${
          form.getValues('pledging') ? 'pledge' : 'poll'
        }...`,
        success:
          form.getValues('pledging') && form.getValues('toPledge')
            ? 'The Pledge will commence within 5 minutes of the stated start time'
            : `Successfully created ${
                form.getValues('pledging') ? 'pledge' : 'poll'
              }`,
        error: (err) =>
          err.error ||
          err.message ||
          `Failed to create ${form.getValues('pledging') ? 'pledge' : 'poll'}`,
      });
      console.log({ res });

      if (!res?.id)
        toast.error(
          `Unexpected error, failed to create ${
            form.getValues('pledging') ? 'pledge' : 'poll'
          }`
        );

      return res?.id;
    } else {
      console.log('createIndividualPoll', { mainPollPayload });
      let createPollAction;
      if (data.company) {
        if (!companyId || !adminUsername)
          return toast.error('You do not belong to a company');
        createPollAction = createCompanyPoll(
          ['id'],
          mainPollPayload,
          dbUser.id
        );
      } else {
        createPollAction = createIndividualPoll(
          ['id'],
          mainPollPayload,
          dbUser.username
        );
      }
      const res: any = await toast.promise(createPollAction, {
        loading: `Creating ${
          form.getValues('pledging') ? 'pledge' : 'poll'
        }...`,
        success:
          form.getValues('pledging') && form.getValues('toPledge')
            ? 'The Pledge will commence within 5 minutes of the stated start time'
            : `Successfully created ${
                form.getValues('pledging') ? 'pledge' : 'poll'
              }`,
        error: (err) => err.error || err.message || 'Failed to create poll',
      });
      console.log(res);

      const id = res?.data?.result?.id;
      if (!id)
        toast.error(
          `Unexpected error, failed to create ${
            form.getValues('pledging') ? 'pledge' : 'poll'
          }`
        );

      return id;
    }
  };

  useEffect(() => {
    if (form.getValues('group')) {
      form.setValue('pledging', false);
    }
  }, [form.getValues('group')]);

  useEffect(() => {
    const init = async () => {
      const data = await getPollById(pollId || duplicateId);
      console.log(isEdit, data);
      setPollData({
        poll: {
          ...data.poll,
          hashtag: hashtags?.filter((item) =>
            data.poll.hashtag?.includes(item.title)
          ),
          category:
            categories?.find((item) => item.title === data.poll.category)
              ?.title || categories[0]?.title,
        },

        subPolls: data.subPolls,
      });

      const parsePoll = async (poll: PollDetail['poll']) => {
        const question = {
          subPollId: isEdit ? poll.id : undefined,
          question: poll.question,
          isNotExact: poll.isNotExact || false,
          maxVote: poll.maxVote || 1,

          options: poll.options
            ? await Promise.all(
                poll.options.map(async (opt) => ({
                  title: opt.title as string,
                  description: opt.description || undefined,
                  embed: opt.detail?.video || undefined,
                  image: opt.detail?.image
                    ? await getImageUrl(opt.detail.image)
                    : undefined,
                  price: opt.detail?.price || undefined,
                  currency: opt.detail?.currency || Currency.SGD,
                }))
              )
            : [],
        };

        // const images = poll.options?.map(async (opt) =>
        //   opt.detail?.image ? await getImageUrl(opt.detail.image) : ''
        // );

        return {
          question,
          // images: {
          //   options: images ? await Promise.all(images) : [],
          // },
        };
      };

      const parsedMain = await parsePoll(data.poll);
      const questions: CreatePollData['questions'] = [parsedMain.question];
      // const images: CreatePollData['images'] = [parsedMain.images];

      if (data.subPolls) {
        await Promise.all(
          data.subPolls.map(async (subPoll) => {
            const parsedSubPoll = await parsePoll(subPoll);

            questions.push(parsedSubPoll.question);
            // images.push(parsedSubPoll.images);
          })
        );
      }
      // startDate: new Date(),
      // endDate:
      //   (dbUser?.tier || Tier.FREE) !== Tier.FREE
      //     ? addMonths(new Date(), 1)
      //     : addMinutes(new Date(), 23 * 60),
      // startTime: addMinutes(new Date(), 10),
      // endTime: addMinutes(new Date(), 5),
      // pledgePeriod: {
      //   immediate: false,
      //   focusedInput: 'startDate',
      //   startDate:
      //     (dbUser?.tier || Tier.FREE) !== Tier.FREE
      //       ? addMonths(new Date(), 1)
      //       : addMinutes(new Date(), 23 * 60),
      //   endDate: addDays(
      //     (dbUser?.tier || Tier.FREE) !== Tier.FREE
      //       ? addMonths(new Date(), 1)
      //       : addMinutes(new Date(), 23 * 60),
      //     7
      //   ),
      //   startTime: addMinutes(new Date(), 6),
      //   endTime: addMinutes(new Date(), 0),
      // },
      const startDate = new Date(data.poll.startTime);
      const endDate = new Date(data.poll.endTime);

      const formPayload: CreatePollData = {
        isEdit: true,
        password: data.poll.password,
        category:
          categories?.find((item) => item.title === data.poll.category)
            ?.title || categories[0]?.title,
        hashtag:
          hashtags?.filter((item) => data.poll.hashtag?.includes(item.title)) ||
          [],
        private:
          data.poll.private !== undefined ? data.poll.private?.toString() : '0',
        company: !!data.poll.companyID,
        title: data.poll.title,
        grc: data.poll.grc || 'Any',
        division: data.poll.division || 'Any',
        video: data.poll.video ? data.poll.video : undefined,
        description: data.poll.description ? data.poll.description : undefined,
        image: data.poll.image
          ? [await getImageUrl(data.poll.image)]
          : undefined,
        period: {
          immediate: false,
          focusedInput: 'startDate',
          startDate: isEdit ? startDate : new Date(),
          startTime: isEdit ? startDate : addMinutes(new Date(), 10),
          endDate: isEdit
            ? endDate
            : (dbUser?.tier || Tier.FREE) !== Tier.FREE
            ? addMonths(new Date(), 1)
            : addMinutes(new Date(), 23 * 60),
          endTime: isEdit ? endDate : addMinutes(new Date(), 5),
        },
        pledgePeriod: {
          immediate: false,
          focusedInput: 'startDate',
          startDate:
            data.poll.pledgePeriod1StartTime && isEdit
              ? new Date(data.poll.pledgePeriod1StartTime)
              : (dbUser?.tier || Tier.FREE) !== Tier.FREE
              ? addMonths(new Date(), 1)
              : addMinutes(new Date(), 23 * 60),
          startTime:
            data.poll.pledgePeriod1StartTime && isEdit
              ? new Date(data.poll.pledgePeriod1StartTime)
              : addMinutes(new Date(), 6),
          endDate:
            data.poll.pledgePeriod1EndTime && isEdit
              ? new Date(data.poll.pledgePeriod1EndTime)
              : addDays(
                  (dbUser?.tier || Tier.FREE) !== Tier.FREE
                    ? addMonths(new Date(), 1)
                    : addMinutes(new Date(), 23 * 60),
                  7
                ),
          endTime:
            data.poll.pledgePeriod1EndTime && isEdit
              ? new Date(data.poll.pledgePeriod1EndTime)
              : addMinutes(new Date(), 0),
        },
        group: data.poll.isGroup || false,
        pledging: data.poll.hasPledge || false,
        questions,
        maxVote: data.poll.maxVote,
        isNotExact: data.poll.isNotExact,
        deletedQuestions: [],
        themeID: data.poll.themeID || 'default',
        isPremium: data.poll.isPremium || false,
      };
      setOldData({
        questions: questions,
        maxVote: data.poll.maxVote || 1,
        isNotExact: data.poll.isNotExact || false,
      });

      form.reset(formPayload);
      console.log(form.getValues('hashtag'));
      setTimeout(() => {
        setR(true);
      });
      console.log('init form', formPayload);
    };
    if ((pollId || duplicateId) && !!hashtags && !!categories) init();
  }, [
    categories,
    hashtags,
    duplicateId,
    pollId,
    form.reset,
    form.setValue,
    setPollData,
    form,
  ]);

  const onDeletePoll = useCallback(async () => {
    const sendDelete = async () => {
      try {
        if (!dbUser) throw new Error('Not logged in');

        const res = await editPoll(
          ['id', 'status'],
          pollId,
          {
            input: {
              id: pollId,
              status: 'CLOSED' as PollStatus,
            },
          },
          dbUser.username
        );
        console.log('onDeletePoll', { res });
        await Promise.all([
          mutate('/poll/feed'),
          mutate(['/poll/owner', user?.username]),
          mutate(['/poll/voted', dbUser?.id]),
        ]);

        history.replace('/recent');
      } catch (err: any) {
        toast.error('Failed to delete poll ', err.message);
      }
    };

    setDeleteModal({
      visible: true,
      onConfirm: async () => {
        setSubmitting(true);
        await sendDelete();
        setSubmitting(false);
        setDeleteModal({
          visible: false,
        });
      },
      onCancel: () => {
        setSubmitting(false);
        setDeleteModal({
          visible: false,
        });
      },
    });
  }, [pollId, history, user, dbUser, setSubmitting, setDeleteModal]);

  const toastError = useCallback((message: string, prefix?: string) => {
    const formattedMessage = prefix
      ? `${capitalizeFirstLetter(prefix)} ${
          message && lowercaseFirstLetter(message)
        }`
      : message;
    console.log(formattedMessage);
    toast.error(formattedMessage);
  }, []);

  const endCurrentPoll = async () => {
    const poll = await endPoll(pollId);
    console.log('thangtesssssssssssttttt', poll);
    if (poll) history.push(`/poll/${pollId}`);
  };

  const onSubmit = async (data: CreatePollData, submit = false) => {
    // if (step === 1 && !submit) return setStep(2);
    // console.log('submit', data, { dirtyFields });

    const now = new Date();
    const start = mergeDateTime(
      data.period.startDate,
      data.period.immediate ? now : data.period.startTime
    );
    const end = mergeDateTime(data.period.endDate, data.period.endTime);
    if (
      dbUser &&
      data.company !== true &&
      !data.isPremium &&
      dbUser.tier === Tier.FREE
    ) {
      const diff = differenceInMinutes(end, start);
      if (diff > 24 * 60) {
        console.error('Maximum poll duration is 24 hours');
        toast.error('Maximum poll duration is 24 hours');
        return;
      }
    }

    if (data.isEdit && isEdit) {
      if (data.maxVote) data.maxVote = +data.maxVote;
      if (pollData === undefined) {
        console.error('Tried to edit poll with no poll data');
        toast.error('Error with poll data, refresh page.');
        return;
      }

      const willReset = checkTriggerReset(data, dirtyFields, pollData, oldData);

      const sendUpdate = async () => {
        try {
          console.log('comp', company);
          if (!dbUser) throw new Error('Not logged in');

          const id = await updatePoll(
            pollId,
            {
              ...data,
              hashtag: data.hashtag.map((item) => item.title),
            },
            dirtyFields,
            pollData,
            dbUser,
            company?.companyID,
            oldData
          );
          if (!id) return;

          await Promise.all([
            mutate('/poll/feed'), // Optimistic update
            await mutate(
              ['/poll/detail', pollId, user === null],
              (cache) => {
                if (!cache || cache.poll?.voteCount === undefined) return cache;

                console.log('mutating poll', pollId, 'vote to 0', {
                  poll: {
                    ...cache.poll,
                    voteCount: 0,
                  },
                  subPolls: cache.subPolls,
                });

                return {
                  poll: {
                    ...cache.poll,
                    voteCount: 0,
                  },
                  subPolls: cache.subPolls,
                };
              },
              false
            ),
            mutate(['/poll/owner', user?.username]),
          ]);
          setCreatedPoll({ id });
        } catch (err: any) {
          console.log('Failed to update poll ', err.message);
          toast.error('Failed to update poll. ' + err.message);
        }
      };

      if (willReset) {
        setConfirmModal({
          visible: true,
          onConfirm: async () => {
            setSubmitting(true);
            await sendUpdate();
            setConfirmModal({
              visible: false,
            });
            setSubmitting(false);
          },
          onCancel: () => {
            setSubmitting(true);
            setConfirmModal({
              visible: false,
            });
            setSubmitting(false);
          },
        });
      } else {
        setSubmitting(true);
        await sendUpdate();
      }
    } else {
      setSubmitting(true);
      try {
        if (!dbUser) throw new Error('Not logged in');
        if (data.company) {
          if (!company?.companyID || !companyAdmin?.owner) {
            await refetch();
            throw new Error('Issue fetching company information, retry.');
          }
        }

        console.log(data, dbUser, company?.companyID, companyAdmin?.owner);
        if (data.toPledge && data.pledging) {
          data.questions[0].options[0].title = data.questions[0].question;
        }
        const id = await createPoll(
          {
            ...data,
            hashtag: data.hashtag.map((item) => item.title),
          },
          dbUser,
          company?.companyID,
          companyAdmin?.owner
        );
        if (!id) return;
        if (!isEdit && form.getValues('toPledge')) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const detailData = await getPollById(id);
          const guest = localStorage.getItem('mighty_poll_guest');

          detailData.poll.options?.forEach((item) => {
            votePoll(
              ['id'],
              id,
              null,
              dbUser.id,
              item.id,
              guest ? true : false
            );
          });
        }
        await Promise.all([
          mutate('/poll/feed'),
          mutate(['/poll/owner', user?.username]),
        ]);
        setCreatedPoll({ id });
      } catch (err: any) {
        setSubmitting(false);
        console.log('Failed to create poll ', err);
      }
      setSubmitting(false);
    }
  };

  const showErrors = (errors, index?: number) => {
    for (const key in errors) {
      if (Array.isArray(errors[key])) {
        errors[key].forEach((err, i) => {
          showErrors(err, i);
        });
      } else if (!errors[key].message && Object.keys(errors[key]).length > 0) {
        showErrors(errors[key]);
      } else {
        // Dont prefix key if message starts with Poll
        toastError(
          errors[key].message,
          errors[key].message.startsWith('Poll')
            ? undefined
            : index !== undefined
            ? `${key} ${index + 1}`
            : key
        );
      }
    }
  };

  const onInvalid = (errors) => {
    console.log('errors', errors);
    if (step === 1) {
      let hasErrors = false;
      for (const key in errors) {
        if (STEP_ONE_FIELDS.includes(key)) {
          if (!errors[key].message && Object.keys(errors[key]).length > 0) {
            showErrors(errors[key]);
          } else {
            toastError(errors[key].message, key);
          }
          hasErrors = true;
        }
      }
      if (!hasErrors) setStep(2);
    } else {
      let missingQn = false;
      let missingOptions = false;
      let titleError = false;

      for (const key in errors) {
        if (key === 'questions') {
          if (errors?.['questions']?.message) {
            toast.error(errors?.['questions']?.message);
          }
          errors?.['questions']?.forEach?.((qnErr, index) => {
            if (qnErr.question?.message) {
              missingQn = true;
            } else if ((qnErr.options?.length || 0) > 0) {
              qnErr.options.forEach((opt) => {
                if (opt.title) {
                  titleError = opt.title.message;
                }
              });
              if (!titleError) {
                missingOptions = true;
              }
            } else if (qnErr.options?.message === 'Required') {
              missingOptions = true;
            } else if (qnErr.options?.message) {
              toast.error(`Question ${index + 1} ${qnErr.options.message}`);
            }
          });
        } else if (key === 'deletedQuestions') {
          toast.error('Error with form deleting question, reload page.');
        } else if (STEP_ONE_FIELDS.includes(key)) {
          if (!errors[key].message && Object.keys(errors[key]).length > 0) {
            showErrors(errors[key]);
          } else {
            toastError(errors[key].message, key);
          }
        } else {
          toastError(key, errors[key].message);
        }
      }

      if (missingQn) {
        toast.error(`Questions must not be blank.`);
      } else if (missingOptions) {
        toast.error(`Please ensure that all question titles are filled.`);
      } else if (titleError) {
        toast.error(`Question titles ${titleError}.`);
      }
    }
  };

  if (createdPoll)
    return (
      <Success
        isEdit={isEdit}
        isPledge={!!form.getValues('pledging')}
        pollId={isEdit ? pollId : createdPoll.id}
      />
    );
  return (
    <>
      <ConfirmDialog
        loading={isSubmitting}
        className="text-white bg-blue shadow-r-salmon-md"
        title="Confirm delete poll"
        content={`This action is irreversible. Once you delete the poll, data and votes will not be recoverable.`}
        open={deleteModal.visible}
        onCancel={
          deleteModal.onCancel
            ? deleteModal.onCancel
            : () =>
                setConfirmModal({
                  visible: false,
                })
        }
        onConfirm={deleteModal.onConfirm}
      />
      <ConfirmDialog
        loading={isSubmitting}
        className="text-white bg-blue shadow-r-salmon-md"
        title="Confirm reset poll votes"
        content={`As your Poll is already created, and you have made  changes made to questions or
        the period these changes will result in the Poll being reset.
        
        All current votes will be lost.`}
        open={confirmModal.visible}
        onCancel={
          confirmModal.onCancel
            ? confirmModal.onCancel
            : () =>
                setConfirmModal({
                  visible: false,
                })
        }
        onConfirm={confirmModal.onConfirm}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <FormProvider {...form}>
          <StepOne
            r={r}
            loading={isEdit && pollData === undefined}
            isSubmitting={isSubmitting}
            isEdit={isEdit}
            isPledge={isPledge}
            categories={categories}
            hashtags={hashtags}
            onDelete={onDeletePoll}
            neon={randomColors}
            pollData={pollData}
            onNext={() => {
              form.handleSubmit((data) => onSubmit(data, false), onInvalid)();
            }}
            end={endCurrentPoll}
            StepTwo={
              <StepTwo
                isEdit={isEdit}
                isSubmitting={isSubmitting}
                neon={randomColors}
                onBack={() => setStep(1)}
                onNext={() => {
                  form.handleSubmit(
                    (data) => onSubmit(data, true),
                    onInvalid
                  )();
                }}
              />
            }
          />
        </FormProvider>
      </form>
    </>
  );
};

export default PollCreatePage;
